import { AsyncPipe } from '@angular/common';
import {
  Component,
  inject,
  Input,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ProvisionCustomReportAction } from '@app/modules/reports/store/reports.actions';
import { GetProductInventoryItemAction } from '@app/modules/service-data/store/product-intentory-item.actions';
import {
  DocumentNotQualifiedType,
  ServiceDataTerminateReason,
} from '@app/modules/service-data/types/service-data.types';
import { CustomReportType, CustomReportTypes } from '@core/core.types';
import { LanguageState } from '@core/store/languages/languages.state';
import { SigneeState } from '@core/store/signees/signees.state';
import { Util } from '@core/utils/core.util';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { SelectOptionComponent } from '@shared/components/select/select-option/select-option.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { ToastService } from '@shared/components/toast-container/toast.service';
import { crossFieldValidator } from '@shared/validators/cross-field-validator';
import { forkJoin } from 'rxjs';
import { catchError, delay, map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-custom-reports-form',
  imports: [
    AsyncPipe,
    FormsModule,
    ReactiveFormsModule,
    SelectComponent,
    SelectOptionComponent,
    TranslatePipe,
  ],
  templateUrl: './custom-reports-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './custom-reports-form.component.scss',
})
export class CustomReportsFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private store = inject(Store);
  private modal = inject(NgbActiveModal);
  private toastService = inject(ToastService);
  @Input({ required: true }) piiIds: string[];
  @Input() preselectedLanguageId?: string;

  protected signees = this.store.selectSignal(SigneeState.getSigneeList);
  protected languages = this.store.selectSignal(LanguageState.getLanguages);
  protected loading = signal(false);

  protected form = this.fb.group(
    {
      id: [Util.CreateGuid()],
      productInventoryItemIds: [null],
      languageId: ['', Validators.required],
      reportType: ['', Validators.required],
      reportReason: [null],
      signeeId: [null],
    },
    {
      validators: [
        crossFieldValidator('reportReason', 'reportType', [
          CustomReportType.canceled,
          CustomReportType.notqualifiedByLaboratory,
          CustomReportType.notqualifiedByPartner,
        ]),
        crossFieldValidator('signeeId', 'reportType', [
          CustomReportType.delayGc,
          CustomReportType.delayReNumber,
          CustomReportType.delayUniReads,
          CustomReportType.delayInconclusive,
          CustomReportType.resample,
          CustomReportType.resampleFf,
          CustomReportType.refund,
        ]),
      ],
    }
  );

  protected reportReason$ = this.form.get('reportType').valueChanges.pipe(
    map((type) => ({
      reasonRequired: !!(
        [
          CustomReportType.canceled,
          CustomReportType.notqualifiedByLaboratory,
          CustomReportType.notqualifiedByPartner,
        ] as string[]
      ).includes(type),
      reasons:
        type === CustomReportType.canceled
          ? Object.values(ServiceDataTerminateReason)
          : Object.values(DocumentNotQualifiedType),
    }))
  );

  ngOnInit() {
    this.form.patchValue({
      productInventoryItemIds: this.piiIds,
      languageId: this.preselectedLanguageId ?? null,
    });
  }

  generate() {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      this.loading.set(true);
      const formValue = this.form.getRawValue();
      const reportData = formValue?.reportType?.split(/ (.*)/s);
      forkJoin(
        formValue.productInventoryItemIds.map((piiId) =>
          this.store
            .dispatch(
              new ProvisionCustomReportAction({
                ...formValue,
                reportType: reportData[0],
                reportDescription:
                  reportData[1] === 'FF' ? 'FetalFraction' : reportData[1],
                productInventoryItemId: piiId,
              })
            )
            .pipe(
              delay(500),
              switchMap(() =>
                this.store.dispatch(
                  new GetProductInventoryItemAction(piiId, true)
                )
              )
            )
        )
      )
        .pipe(
          catchError((err) => {
            this.loading.set(false);
            throw err;
          })
        )
        .subscribe(() => {
          this.toastService.success(
            'Custom report uploaded successfully',
            'Success'
          );
          this.modal.dismiss();
        });
    }
  }

  dismiss() {
    this.modal.dismiss();
  }

  protected readonly CustomReportTypes = CustomReportTypes;
}
